import React from "react"

import {
  getCustomerInvitationListQueryKey,
  useCustomerInvitationConfig,
  useCustomerInvitationCreate,
} from "@boundless/bl-api-client/customer/services"
import { CustomerInvitationCreateBody } from "@boundless/bl-api-client/customer/services.zod"
import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { HandshakeIcon, SendIcon } from "lucide-react"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { FormInput } from "@/components/form-elements/FormInput"
import {
  FormTurnstile,
  type FormTurnstileHandle,
} from "@/components/form-elements/FormTurnstile"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

type FormSchema = z.infer<typeof CustomerInvitationCreateBody>

export function CreateCustomerReferralDialog() {
  const queryClient = useQueryClient()
  const [open, setOpen] = React.useState(false)

  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()
  const turnstileRef = React.useRef<FormTurnstileHandle>(null)
  const turnstileEnabled =
    boundlessConfig.referralCaptchaEnabled &&
    Boolean(boundlessConfig.turnstileSiteKey)

  const {
    data: config,
    isSuccess,
    queryKey: configQueryKey,
  } = useCustomerInvitationConfig()

  const isOutOfReferrals = !(isSuccess && config.referralsLeft > 0)

  const form = useForm<FormSchema>({
    resolver: zodResolver(
      CustomerInvitationCreateBody.extend({
        captcha: z.string().min(0),
      }),
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      captcha: "",
    },
  })
  const captchaToken = useWatch({ control: form.control, name: "captcha" })

  const { mutate: inviteUser, isPending } = useCustomerInvitationCreate({
    mutation: {
      onSuccess: (data) => {
        const listQueryKey = getCustomerInvitationListQueryKey()
        queryClient.invalidateQueries({ queryKey: listQueryKey })
        queryClient.invalidateQueries({ queryKey: configQueryKey })

        toast.success("Invitation sent successfully!", {
          description: `${data.recipient.firstName} will receive an email shortly.`,
        })
        form.reset()
        setOpen(false)
      },
      onError: (error) => {
        turnstileRef.current?.reset()
        if (error.status === 400) {
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        } else {
          toast.error("Error creating referral", {
            description: error.message,
          })
        }
      },
    },
  })

  const onSubmit = (data: FormSchema) => {
    inviteUser({ data: { ...data, isReferral: true } })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={isOutOfReferrals}>
          <HandshakeIcon className="size-6" />
          Refer a Friend
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refer a friend</DialogTitle>
          <DialogDescription>
            {"Earn rewards for your referrals - it's that simple!"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormInput name="firstName" placeholder="First Name" />
            <FormInput name="lastName" placeholder="Last Name" />
            <FormInput name="email" placeholder="Email" type="email" />

            {turnstileEnabled && (
              <FormTurnstile
                ref={turnstileRef}
                action="customer_referral"
                className="mx-auto"
                name="captcha"
                siteKey={boundlessConfig.turnstileSiteKey}
              />
            )}

            <DialogFooter>
              {isPending ? (
                <LoadingButton />
              ) : (
                <Button
                  type="submit"
                  disabled={turnstileEnabled && !captchaToken}
                >
                  <SendIcon />
                  Send
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
